import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, FileText, Users, Megaphone, Box, Command } from "lucide-react";

const KIND_ICON = {
  record: Box,
  sop: FileText,
  employee: Users,
  marketing: Megaphone,
};

const KIND_LABEL = {
  record: "Record",
  sop: "SOP",
  employee: "Employee",
  marketing: "Marketing",
};

/**
 * Global command palette. Opens with ⌘K / Ctrl+K.
 * Searches records, SOPs, employees, marketing across the org.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const runSearch = useCallback((query) => {
    if (!query || query.length < 2) { setResults([]); return; }
    api.get(`/search?q=${encodeURIComponent(query)}`)
      .then((r) => { setResults(r.data.results); setActive(0); })
      .catch(() => setResults([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 200);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  const goTo = (r) => {
    setOpen(false);
    nav(r.url);
  };

  const onKeyDownInput = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); goTo(results[active]); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger"
        className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-sm border border-border bg-card hover:bg-secondary transition-colors text-xs text-muted-foreground min-w-[220px]"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Search everything…</span>
        <kbd className="ml-auto font-mono-plex text-[10px] tracking-widest border border-border px-1.5 py-0.5 rounded-sm">
          <Command className="w-2.5 h-2.5 inline mr-0.5" />K
        </kbd>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
      onClick={() => setOpen(false)}
      data-testid="command-palette"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-card dark:bg-card border border-border rounded-sm shadow-2xl overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDownInput}
            placeholder="Search records, SOPs, employees, marketing…"
            data-testid="command-palette-input"
            className="flex-1 bg-transparent outline-none text-base"
          />
          <kbd className="font-mono-plex text-[10px] tracking-widest border border-border px-1.5 py-0.5 rounded-sm text-muted-foreground">
            ESC
          </kbd>
        </div>
        <div className="max-h-96 overflow-auto">
          {q.length < 2 ? (
            <div className="p-10 text-center text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              Type at least 2 characters
            </div>
          ) : results.length === 0 ? (
            <div className="p-10 text-center text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              No results for &ldquo;{q}&rdquo;
            </div>
          ) : (
            <ul>
              {results.map((r, i) => {
                const Icon = KIND_ICON[r.kind] || Box;
                return (
                  <li key={`${r.kind}-${r.id}`}>
                    <button
                      onClick={() => goTo(r)}
                      onMouseEnter={() => setActive(i)}
                      data-testid={`cmd-result-${r.kind}-${r.id}`}
                      className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 flex items-center gap-3 ${
                        i === active ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.title}</div>
                        {r.subtitle && (
                          <div className={`text-xs truncate ${i === active ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            {r.subtitle}
                          </div>
                        )}
                      </div>
                      <span className={`overline ${i === active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                        {KIND_LABEL[r.kind]}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-4 py-2 border-t border-border text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground flex items-center justify-between">
          <span>↑↓ Navigate · ↵ Open</span>
          <span>{results.length} results</span>
        </div>
      </div>
    </div>
  );
}
